import type { RefCallback } from 'react';
import { useCallback, useEffect, useState } from 'react';
import useLocalStorageState from 'use-local-storage-state';

import type { DataTableItemType } from '@adflow/shared/util-column-templates';

const ROW_HEIGHT = 56;
const TABLE_HEADER = 34;

const MIN_ROWS = 5;
const MAX_ROWS = 100;

export interface UseRowsPerPageOptions {
	itemType: DataTableItemType;
	rowHeight?: number;

	/**
	 * This is bad design! We should use some kind of explicit state management instead of this,
	 * but I (Joshua Shaffer) did not have time to do that when this was written.
	 *
	 * There is a problem with using a local useState on the products and users list/view pages.
	 * The full size table and the sidebar views are different components. Swapping them deletes
	 * and re-creates calculatedRowsPerPage causing it to recalculate. This can cause a different
	 * number of rows to be used on each component which then can cause the selected item to be
	 * on a different page. calculatedRowsPerPage had been transferred between components via a
	 * URL parameter. One can supply a hook to interact with the parameter here so that
	 * calculatedRowsPerPage is kept like before.
	 */
	useCalculatedRowsPerPageState?: () => readonly [
		calculatedRowsPerPage: number | null | undefined,
		setCalculatedRowsPerPage: (value: number | null | undefined) => void,
	];
}

export interface UseRowsPerPageResult {
	/** The currently set rows per page. Will be null if it is calculating. */
	rowsPerPage: number | null;

	/** Set the preferred rows per page. Set to null or undefined to auto-calculate rows per page for the current table size. */
	setRowsPerPage: (newItemsPerPage: number | null | undefined) => void;

	/**
	 * Ref to the scrolling area of the table.
	 * This is expected to contain the table header, table rows, and nothing else.
	 * If this is not placed on the scrolling element the scrollbar widths cannot be accounted for.
	 */
	tableWrapRef: RefCallback<HTMLElement | null>;
}

/**
 * This hook is in charge of handling the logic behind the number of rows
 * per page (RPP) in a given data table.
 *
 * There are three data sources used in determining what RPP to use:
 * - The dom rect height of the table
 * - The RPP stored in local storage
 * - The RPP stored in the URL query param
 *
 * In order of priority:
 * 1. URL query param (if the user A shares a link to user B, user B should
 * 		see exactly what user A sees)
 * 2. Local storage (if user C sets the RPP for customers table to 20, goes
 * 		to another page, then comes back to the customers table, it should
 * 		still be 20)
 * 3. The table height (the RPP is set to fit as many rows as possible without
 * 		overflowing the table)
 *
 * If the state value for a given data source is null/undefined, the RPP
 * used should be the next priority data source. EX: if there is no RPP
 * in the Url query param, the local storage RPP should be used.
 */
export function useRowsPerPage({
	itemType,
	rowHeight = ROW_HEIGHT,
	useCalculatedRowsPerPageState = useState,
}: UseRowsPerPageOptions): UseRowsPerPageResult {
	const [preferredRowsPerPage, setPreferredRowsPerPage] =
		useLocalStorageState<number>(`items-per-page:${itemType}`);

	const [calculatedRowsPerPage, setCalculatedRowsPerPage] =
		useCalculatedRowsPerPageState();

	const [tableWrapElement, tableWrapRef] = useState<HTMLElement | null>(null);

	useEffect(() => {
		// Don't try calculating rows per page when:
		// - the user has a specific preference
		if (
			typeof preferredRowsPerPage === 'number' &&
			typeof calculatedRowsPerPage !== 'number'
		) {
			setCalculatedRowsPerPage(preferredRowsPerPage);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [calculatedRowsPerPage]);

	useEffect(() => {
		// Don't try calculating rows per page when:
		// - the user has a specific preference
		// - we have already calculated rows per page
		if (
			typeof preferredRowsPerPage === 'number' ||
			typeof calculatedRowsPerPage === 'number'
		) {
			// We don't need to calculate ideal rows per page.
			return;
		}

		if (tableWrapElement === null) {
			// We don't have tableWrapElement so we cannot calculate ideal rows per page.
			return;
		}

		let animationHandle: number | undefined = undefined;
		const resizeObserver = new ResizeObserver((entries) => {
			animationHandle = requestAnimationFrame(() => {
				for (const entry of entries) {
					// This height will account for the space the scroll bar at the bottom
					// takes up IF tableWrapRef is applied to the scrolling area.
					//
					// The table's header MUST have the same width as the table's rows so
					// that we can tell if there will be a horizontal scroll bar before
					// the table's rows are rendered.
					const tableHeight = entry.contentRect.height;

					if (tableHeight > 0) {
						const availableHeight = tableHeight - TABLE_HEADER;

						const newRowsPerPage = Math.min(
							Math.max(Math.floor(availableHeight / rowHeight), MIN_ROWS),
							MAX_ROWS
						);

						setCalculatedRowsPerPage(newRowsPerPage);
						return;
					}
				}
			});
		});

		resizeObserver.observe(tableWrapElement);

		return () => {
			if (animationHandle !== undefined) {
				cancelAnimationFrame(animationHandle);
			}
			resizeObserver.disconnect();
		};
	}, [
		calculatedRowsPerPage,
		rowHeight,
		preferredRowsPerPage,
		tableWrapElement,
		setCalculatedRowsPerPage,
	]);

	return {
		rowsPerPage: calculatedRowsPerPage ?? null,

		setRowsPerPage: useCallback(
			(newItemsPerPage: number | null | undefined) => {
				if (typeof newItemsPerPage === 'number') {
					setPreferredRowsPerPage(newItemsPerPage);
					setCalculatedRowsPerPage(newItemsPerPage);
				} else {
					setPreferredRowsPerPage(undefined);
					setCalculatedRowsPerPage(undefined);
				}
			},
			[setCalculatedRowsPerPage, setPreferredRowsPerPage]
		),

		tableWrapRef,
	};
}
